import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../store/hooks";
import { logoutUser } from "../store/authSlice";
import { apiService } from "../services/api";

type SubscriptionStatus = {
  status: string;
  isActive: boolean;
  currentPeriodEnd: string | null;
  trialEndDate: string | null;
  gracePeriodEndDate?: string | null;
  cancelAtPeriodEnd: boolean;
  hasStripeCustomer: boolean;
  hasStripeSubscription: boolean;
  hasPaymentMethod: boolean;
  isInTrial: boolean;
  isInGracePeriod?: boolean;
  trialDaysRemaining: number;
  gracePeriodDaysRemaining?: number;
};

const HEADER_HEIGHT_REM = 5; // Tailwind h-20 -> 5rem

const SubscriptionOverlay: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [hasUnauthorizedError, setHasUnauthorizedError] = useState(false);

  const isBlocked = (s: SubscriptionStatus | null) => {
    if (!s) return false;

    // Block when:
    // 1. Status is explicitly 'INACTIVE' (trial expired and grace period ended)
    // 2. OR when user is not active, not in trial, and not in grace period
    return (
      s.status === "INACTIVE" ||
      (!s.isActive && !s.isInTrial && !s.isInGracePeriod)
    );
  };

  const fetchStatus = useCallback(async () => {
    // Don't fetch if user is not authenticated, on sign-in page, or we've already encountered unauthorized error
    if (
      !isAuthenticated ||
      !user ||
      location.pathname === "/sign_in" ||
      hasUnauthorizedError
    ) {
      return;
    }

    try {
      setLoading(true);
      const res = await apiService.getSubscriptionStatus();
      setStatus(res as any);
    } catch (e: any) {
      // If we get a 401 Unauthorized, clear all storage and redirect to sign-in
      if (e?.response?.status === 401) {
        console.error(
          "Unauthorized access - clearing storage and redirecting to sign-in"
        );
        setHasUnauthorizedError(true);
        apiService.clearAllStorage();
        dispatch(logoutUser());
        navigate("/sign_in", { replace: true });
        return;
      }
      // Silently handle other errors
    } finally {
      setLoading(false);
    }
  }, [
    isAuthenticated,
    user,
    location.pathname,
    hasUnauthorizedError,
    navigate,
    dispatch,
  ]);

  useEffect(() => {
    // Only fetch if user is authenticated and not on sign-in page
    if (
      !isAuthenticated ||
      !user ||
      location.pathname === "/sign_in" ||
      hasUnauthorizedError
    ) {
      return;
    }

    fetchStatus();
    const id = setInterval(fetchStatus, 60_000);
    return () => clearInterval(id);
  }, [
    fetchStatus,
    isAuthenticated,
    user,
    location.pathname,
    hasUnauthorizedError,
  ]);

  const openPortal = async () => {
    try {
      setCreating(true);
      const returnUrl = `${window.location.origin}/dashboard`;
      const portal = await apiService.createCustomerPortalSession(returnUrl);
      window.location.href = (portal as any).url;
    } finally {
      setCreating(false);
    }
  };

  const createSubscription = async () => {
    try {
      setCreating(true);
      await apiService.createSubscription();
      await fetchStatus();
      // If still not active, fallback to portal
      if (status && isBlocked(status)) {
        await openPortal();
      }
    } finally {
      setCreating(false);
    }
  };

  if (loading || !isBlocked(status)) return null;

  return (
    <div
      className="fixed left-0 right-0 bottom-0 z-50"
      style={{ top: `${HEADER_HEIGHT_REM}rem` }}
    >
      {/* Backdrop + blur over everything except header */}
      <div className="absolute inset-0 bg-white/60 backdrop-blur-sm" />

      {/* Centered modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-[520px] rounded-2xl border border-lighterGray bg-lightestGray shadow-xl">
          <div className="px-6 py-5 border-b border-lighterGray">
            <h3 className="text-h2 font-bold text-darkestSlate">
              Your trial has ended
            </h3>
          </div>
          <div className="px-6 py-5 text-darkGray text-[14px] leading-relaxed">
            {!status?.hasPaymentMethod ? (
              <p>
                To continue using MyHomey, please add a payment method and pay
                your invoice.
              </p>
            ) : (
              <p>
                Your trial has ended. Click "Pay Invoice" to activate your
                subscription and continue using MyHomey.
              </p>
            )}
          </div>
          <div className="px-6 pb-6 flex gap-3 justify-end">
            {!status?.hasPaymentMethod ? (
              <button
                onClick={openPortal}
                disabled={creating}
                className="px-4 py-2 rounded-lg bg-[#000000] text-white text-sm font-semibold disabled:opacity-50"
              >
                {creating ? "Opening…" : "Add Payment Method"}
              </button>
            ) : (
              <button
                onClick={createSubscription}
                disabled={creating}
                className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-semibold disabled:opacity-50"
              >
                {creating ? "Processing…" : "Pay Invoice"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionOverlay;
